/**
 * FlagIcon - renders a country flag using flagcdn.com's free SVG CDN.
 *
 * Regional-indicator emoji flags don't render on Windows (which is almost
 * all of our dev + target users). This component gives us a consistent
 * flag visual across macOS, Windows, Linux, and headless email clients
 * without shipping a 150KB sprite sheet.
 *
 * Use width=20 height=14 for list/combobox items so the two-letter code
 * stays balanced with the text beside it.
 */
import { memo } from 'react'
import { cn } from '@/lib/cn'

export interface FlagIconProps {
  /** ISO-3166 alpha-2 country code (case-insensitive). */
  code: string | null | undefined
  /** Rendered width in px. Height auto-scales 20×14 aspect. */
  width?: number
  className?: string
}

export const FlagIcon = memo(function FlagIcon({ code, width = 20, className }: FlagIconProps) {
  const c = String(code || '').toLowerCase()
  if (!c || c.length !== 2) {
    return (
      <span
        aria-hidden
        className={cn('inline-block rounded-[2px] bg-muted', className)}
        style={{ width, height: Math.round((width * 14) / 20) }}
      />
    )
  }
  const h = Math.round((width * 14) / 20)
  return (
    <img
      src={`https://flagcdn.com/w${width * 2}/${c}.png`}
      srcSet={`https://flagcdn.com/w${width * 2}/${c}.png 2x`}
      width={width}
      height={h}
      alt=""
      aria-hidden
      loading="lazy"
      className={cn('inline-block rounded-[2px] object-cover', className)}
      style={{ width, height: h }}
    />
  )
})
